import { translate } from 'game/translate';
import './styles.css';
import { useDispatch, useSelector } from 'react-redux';
import { UIRootState } from 'ui/store';
import { useCallback, useMemo, useRef } from 'react';
import { openMissionCompletePopup } from 'ui/store/actions/gameUIActions';
import { SoundManager } from 'game/core/soundManager';

interface MissionStatusBadgeProps {
    className?: string;
    type?: 'default' | 'no-missions';
    height?: string;
}

export const MissionStatusBadge: React.FC<MissionStatusBadgeProps> = ({ 
    className = '' ,
    type = 'default',
    height = '120px',
}) => {
    const dispatch = useDispatch();

    
    const dailyMission = useSelector((state: UIRootState) => {
        if (type === 'no-missions' && !state.gameUI.missions.dailyMission?.rewardsClaimed &&state.gameUI.missions.dailyMission?.isAdditionalMission || type === 'default') {
            return state.gameUI.missions.dailyMission;
        }

        return undefined;
    });

    const completedMissions = useMemo(() => dailyMission?.actions.filter(a => a.passed).length ?? 0, [dailyMission]);

    const progressPercentage = useMemo(() => completedMissions / (dailyMission?.actions.length ?? 0) * 100, [dailyMission]);

    const isAllMissionsCompleted = useMemo(() => completedMissions === (dailyMission?.actions.length ?? 0), [completedMissions, dailyMission]);

    const handleCustomButtonClick = (buttonKey: string, data?: any) => {
        document.dispatchEvent(new CustomEvent('ui-button-click', {
          detail: { buttonKey, data }
        }));
    };

    const chestImage = useMemo(() => {
        if (!dailyMission?.rewardsClaimed && dailyMission?.isAdditionalMission) {
            return { src: 'treasure_chest_2', size: 92 };
        }

        if (dailyMission?.rewardsClaimed && dailyMission?.isAdditionalMission) {
            return { src: 'treasure_chest_2_open', size: 90 };
        }

        if (dailyMission?.rewardsClaimed && !dailyMission?.isAdditionalMission) {
            return { src: 'chest_open', size: 90 };
        }

        if (!dailyMission?.rewardsClaimed && !dailyMission?.isAdditionalMission) {
            return { src: 'reward_box_small', size: 90 };
        }
        return { src: 'chest_open', size: 90 };
    }, [type, dailyMission]);

    const rewardSmallBoxImage = useMemo(() => {
        if (dailyMission?.isAdditionalMission) {
            return 'reward_box_small_2_secondary';
        }

        return 'reward_box_small_secondary';
    }, [type]);

    const handleClaimButtonClick = useCallback(() => {
        SoundManager.play('missionClaimRewardsSFX');
        dispatch(openMissionCompletePopup('sidebar'));
        handleCustomButtonClick('mission-claim-rewards-daily');
    }, [dispatch]);

    const marginRight = useMemo(() => {
        if ((type === 'default' && dailyMission?.rewardsClaimed) || type === 'no-missions') {
            return '24px';
        }
        return '12px';
    }, [type, dailyMission]);

    if (!dailyMission && type === 'default') {
        return null;
    }
    
    return (
        <div style={{ height }} className={`mission-status-badge ${className}`}>
            <div className="mission-status-badge-background">
                <picture>
                    <source srcSet="/assets/missions/mission_status_bar.webp" type="image/webp" />
                    <img className="mission-status-badge-background-image" src="/assets/missions/mission_status_bar.png"/>
                </picture>
            </div>
            <div style={{ height: '100%', position: 'relative', top: !isAllMissionsCompleted ? '-100%' : '-95%' }}>
                <div style={{ marginRight, justifyContent: 'space-between' }} className={`mission-status-badge-progress-bar${dailyMission && !isAllMissionsCompleted && type === 'default' ? ' mission-status-badge-progress-bar--no-margin-top' : ''}`}>
                    <div style={type === 'default' ? { width: 'max-content' } : {}} className={`mission-status-badge-progress-bar-wrapper${type === 'no-missions' && !dailyMission ? ' mission-status-badge-progress-bar-wrapper--with-gap' : ''}`}>
                        {dailyMission && !dailyMission.rewardsClaimed && !isAllMissionsCompleted && <span className="mission-status-badge-content-text">
                            {translate('complete_all_missions_bar_text')}
                        </span>}

                        {dailyMission && !dailyMission.rewardsClaimed && isAllMissionsCompleted && <span className="mission-status-badge-content-text">
                            {translate('all_mission_completed_bar_text')}
                        </span>}


                        {type === 'default' && dailyMission?.rewardsClaimed && <span className="reward-claimed-text">
                            {translate('reward_claimed_text')}
                        </span>}

                        {type === 'no-missions' && !dailyMission && <span className="mission-status-badge-content-text">
                            {translate('more_missions_text')}
                        </span>}

                        {!dailyMission && type === 'no-missions' && <div style={{ position: 'inherit' }} className="mission-status-badge-reward-box-opened mission-status-badge-reward-box-opened--no-missions">
                        <div id="closing-ignore-el" style={{ maxWidth: 'unset'}} onClick={() => handleCustomButtonClick('new-missions-request')} className="mission-status-badge-claim-btn mission-status-badge-claim-btn--no-missions">
                            <svg xmlns="http://www.w3.org/2000/svg" width="19" height="18" viewBox="0 0 19 18" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M16.8125 7.03125H2.1875V13.3529C2.1875 14.3661 3.00889 15.1875 4.02211 15.1875H14.9779C15.9911 15.1875 16.8125 14.3661 16.8125 13.3529V7.03125ZM8.82773 8.22325C8.64278 8.08098 8.375 8.21283 8.375 8.44618V13.1748C8.375 13.4143 8.65513 13.5442 8.83792 13.3895L11.7652 10.9126C11.902 10.7968 11.8971 10.5843 11.755 10.475L8.82773 8.22325Z" fill="white"/>
                                <path d="M2.1875 4.64711C2.1875 3.63388 3.00888 2.8125 4.02211 2.8125H7.76973L5 5.90625H2.1875V4.64711Z" fill="white"/>
                                <path d="M6.44902 5.90625L9.21875 2.8125H12.8322L10.0625 5.90625H6.44902Z" fill="white"/>
                                <path d="M11.5115 5.90625H16.8125V4.64711C16.8125 3.63388 15.9911 2.8125 14.9779 2.8125H14.2812L11.5115 5.90625Z" fill="white"/>
                            </svg>
                            {translate('unlock_missions_text')}
                            </div>
                        </div>}
                        
                        {type === 'default' && <div className="mission-progress-container">
                            {dailyMission && !dailyMission.rewardsClaimed && type === 'default' && <div className="mission-progress-bar">
                                <div 
                                    className="mission-progress-fill"
                                    style={{ width: `calc(${progressPercentage}% - 2px)` }}
                                />
                                <span className="mission-progress-text">
                                    {completedMissions}/{dailyMission?.actions.length ?? 0}
                                </span>
                            </div>}
                            

                            <>
                                {isAllMissionsCompleted && dailyMission && !dailyMission.rewardsClaimed && <div style={{ left: 'calc(100% - 35px)', width: 'fit-content' }} className="mission-status-badge-reward-box-small">
                                    <picture>
                                        <source srcSet={`/assets/missions/${rewardSmallBoxImage}.webp`} type="image/webp" />
                                        <img style={{ height: '44px', width: '44px' }} src={`/assets/missions/${rewardSmallBoxImage}.png`}/>
                                    </picture>
                                </div>}
                                {isAllMissionsCompleted && dailyMission && !dailyMission.rewardsClaimed && <img style={{ left: 'calc(100% - 44px)', width: 'fit-content' }} className="mission-status-badge-glow-small" src="/assets/missions/glow_small.png"/>}
                            </>

                        </div>}
                    </div>

                    {!isAllMissionsCompleted && <div style={{ position: 'inherit' }} className="mission-status-badge-reward-box">
                        <picture>
                            <source srcSet={`/assets/missions/${chestImage.src}.webp`} type="image/webp" />
                            <img style={{ height: chestImage.size }} src={`/assets/missions/${chestImage.src}.png`}/>
                        </picture>
                    </div>}

                    {isAllMissionsCompleted && dailyMission?.rewardsClaimed && <div style={{ position: 'inherit' }} className="mission-status-badge-reward-box-opened">
                        <picture>
                            <source srcSet={`/assets/missions/${chestImage.src}.webp`} type="image/webp" />
                            <img style={{ height: chestImage.size }} src={`/assets/missions/${chestImage.src}.png`}/>
                        </picture>
                    </div>}


                    {!dailyMission && type === 'no-missions' && <div style={{ position: 'inherit' }} className="mission-status-badge-reward-box-opened">
                        <picture>
                            <source srcSet={`/assets/missions/additional_missions_icon.webp`} type="image/webp" />
                            <img style={{ width: '116px', height: '85px' }} src={`/assets/missions/additional_missions_icon.png`}/>
                        </picture>
                    </div>}
                    

                    {isAllMissionsCompleted && dailyMission && !dailyMission.rewardsClaimed && <div style={{ position: 'inherit' }} className="mission-status-badge-reward-box-opened">
                        <div id="closing-ignore-el" onClick={handleClaimButtonClick} className="mission-status-badge-claim-btn">{translate('claim_btn_text')}</div>
                    </div>}
                    
                </div>
            </div>  
        </div>
    );
}